/**
 * Service credentials — the operator's issue / list / revoke surface.
 *
 * The plaintext token comes back from `issue()` ONCE and is never stored: not
 * in the query cache, not in localStorage, not in a URL. The database holds
 * only salt + hash, so re-display is cryptographically impossible rather than
 * merely withheld — which is why the UI has to make the operator confirm they
 * saved it before closing the dialog.
 */

import { TonnieClient } from '../client/base';
import { API_ENDPOINTS } from '../client/config';

export type ServiceCredentialScope = 'cohort:read';
export type ServiceCredentialStatus = 'active' | 'expired' | 'revoked';

/** What a list or detail read returns. Never the secret, hash or salt. */
export interface MaskedServiceCredential {
  id: string;
  name: string;
  /** e.g. `tnsc_live_3k7q9w2m4x8p` — safe to display and to log. */
  tokenPrefix: string;
  lastFour: string;
  scopes: ServiceCredentialScope[];
  allCohorts: boolean;
  cohortIds: string[];
  expiresAt: string;
  revokedAt: string | null;
  revokeReason: string | null;
  createdBy: string;
  createdAt: string;
  lastUsedAt: string | null;
  lastUsedIp: string | null;
  status: ServiceCredentialStatus;
}

export interface IssueServiceCredentialInput {
  name: string;
  scopes: ServiceCredentialScope[];
  cohortMode: 'all' | 'list';
  cohortIds?: string[];
  expiresInDays: number;
}

export function createServiceCredentialsService(client: TonnieClient) {
  return {
    async list(): Promise<MaskedServiceCredential[]> {
      const res = await client.get<{ credentials: MaskedServiceCredential[] }>(
        API_ENDPOINTS.BUSINESS.SERVICE_CREDENTIALS
      );
      return res.credentials;
    },

    /**
     * The ONLY call that ever returns a plaintext token. Callers must hand it
     * straight to the reveal dialog and drop it — never cache it.
     */
    async issue(
      input: IssueServiceCredentialInput
    ): Promise<{ credential: MaskedServiceCredential; token: string }> {
      return client.post<{ credential: MaskedServiceCredential; token: string }>(
        API_ENDPOINTS.BUSINESS.SERVICE_CREDENTIALS,
        input
      );
    },

    async revoke(id: string, reason?: string): Promise<MaskedServiceCredential> {
      const res = await client.post<{ credential: MaskedServiceCredential }>(
        API_ENDPOINTS.BUSINESS.SERVICE_CREDENTIAL_REVOKE(id),
        reason ? { reason } : {}
      );
      return res.credential;
    },
  };
}

export type ServiceCredentialsService = ReturnType<typeof createServiceCredentialsService>;
